import { FC, Fragment } from 'react';
import classNames from 'classnames';
import { useLocale } from '@app/core/hooks/useLocale';
import { useAppSelector } from '@core/hooks/redux-hooks';
import SvgChevron from '@app/shared/icons/Chevron';
import { Pill, PillSize, PillTheme } from '@app/shared/components/pill/Pill';
import { InstrumentDefinitionWithId } from '@app/core/store/capital-structure-slice-selectors';
import { CaseDto } from '@app/shared/models/contracts/project-dto';
import DiscountRateOverrideCategoryRows from './DiscountRateOverrideCategoryRows';
import { OverrideField } from './useDiscountRateOverride';
import OriginalDiscountRateRows from './OriginalDiscountRateRows';
import {
  CaseStructureItem,
  fieldHasAnyOverrides,
  instrumentHasTranches,
  instrumentHasTransactions,
  isInstrumentOverridden,
} from './discountRateOverrideUtils';
import { TrancheKey } from '@app/shared/models/contracts/enums/shared-enums';

interface InstrumentSectionProps {
  instrument: InstrumentDefinitionWithId;
  columnsCount: number;
  initializedCases: CaseDto[];
  casesStructure: CaseStructureItem[];
  findSortedCaseIndex: (caseId: string) => number;
  handleRevertCategory: (
    instrumentId: string,
    field: OverrideField,
    trancheId?: Nullable<string>
  ) => void;
  isExpanded: boolean;
  toggleInstrumentExpanded: (instrumentId: string) => void;
}

const DiscountRateOverrideInstrumentSection: FC<InstrumentSectionProps> = ({
  instrument,
  columnsCount,
  initializedCases,
  casesStructure,
  findSortedCaseIndex,
  handleRevertCategory,
  isExpanded,
  toggleInstrumentExpanded,
}) => {
  const { l } = useLocale();
  const results = useAppSelector((state) => state.pwerm2Calculation.calculatedResults);
  const showOriginalDiscountRates = useAppSelector(
    (state) => state.uiValues.showOriginalDiscountRates
  );

  const { hasInfusions, hasOtherInterimCashflows } = instrumentHasTransactions(
    results,
    instrument.instrumentId
  );

  const hasTranches = instrumentHasTranches(instrument);

  const categoryRowProps = {
    initializedCases,
    casesStructure,
    findSortedCaseIndex,
    handleRevertCategory,
  };

  const trancheIds = hasTranches
    ? instrument.payoutLogic?.map((tranche) => tranche.id) ?? []
    : [TrancheKey.DefaultTrancheKey];

  const isOverridden = isInstrumentOverridden(
    initializedCases,
    instrument.instrumentId,
    hasInfusions,
    hasOtherInterimCashflows,
    trancheIds
  );

  const shouldShowOriginalRates = (field: OverrideField, trancheId: Nullable<string> = null) => {
    if (
      showOriginalDiscountRates &&
      fieldHasAnyOverrides(initializedCases, instrument.instrumentId, field, trancheId)
    ) {
      return true;
    }

    return false;
  };

  return (
    <>
      <tr
        onClick={() => toggleInstrumentExpanded(instrument.instrumentId)}
        aria-expanded={isExpanded}
        data-testid={`discount-rate-override-instrument-${instrument.instrumentId}`}>
        <th className="table-primary__cell--header">
          <span
            className={classNames('table-primary__table-section-indicator', {
              'table-primary__table-section-indicator--rotated': !isExpanded,
            })}>
            <SvgChevron className="table-primary__expansion-toggle" />
          </span>
          {instrument.instrumentNarrative}
        </th>
        <th colSpan={columnsCount - 1} className="table-primary__cell--header">
          <span style={{ visibility: isOverridden ? 'visible' : 'hidden' }}>
            <Pill size={PillSize.Xsmall} noTextTransform theme={PillTheme.Orange}>
              {l('_Overridden')}
            </Pill>
          </span>
        </th>
      </tr>

      {isExpanded && (
        <>
          {hasInfusions && (
            <>
              <DiscountRateOverrideCategoryRows
                instrumentId={instrument.instrumentId}
                field="infusionDiscountRateOverride"
                label={l('_Infusions')}
                {...categoryRowProps}
              />
              {shouldShowOriginalRates('infusionDiscountRateOverride') && (
                <OriginalDiscountRateRows
                  instrumentId={instrument.instrumentId}
                  field="infusionDiscountRateOverride"
                  casesStructure={casesStructure}
                  initializedCases={initializedCases}
                  findSortedCaseIndex={findSortedCaseIndex}
                />
              )}
            </>
          )}

          {hasOtherInterimCashflows && (
            <>
              <DiscountRateOverrideCategoryRows
                instrumentId={instrument.instrumentId}
                field="otherInterimsDiscountRateOverride"
                label={l('_OtherInterimCashFlows')}
                {...categoryRowProps}
              />
              {shouldShowOriginalRates('otherInterimsDiscountRateOverride') && (
                <OriginalDiscountRateRows
                  instrumentId={instrument.instrumentId}
                  field="otherInterimsDiscountRateOverride"
                  casesStructure={casesStructure}
                  initializedCases={initializedCases}
                  findSortedCaseIndex={findSortedCaseIndex}
                />
              )}
            </>
          )}

          {hasTranches ? (
            <>
              <tr
                data-testid={`exitDistributionByTrancheDiscountRateOverrides-row-${instrument.instrumentId}`}>
                <td className="table-primary__cell--vertical-separator">
                  <span>{l('_ExitDistributions')}</span>
                </td>
                {casesStructure.map((caseItem) =>
                  caseItem.forecasts.map((forecast, forecastIndex) => (
                    <td
                      key={forecast.forecastId + '-exit-distributions-header'}
                      className={classNames({
                        'table-primary__cell--vertical-separator':
                          caseItem.forecasts.length - 1 === forecastIndex,
                      })}
                    />
                  ))
                )}
              </tr>
              {instrument.payoutLogic?.map((tranche, trancheIndex) => (
                <Fragment key={`exit-tranche-${instrument.instrumentId}-${trancheIndex}`}>
                  <DiscountRateOverrideCategoryRows
                    instrumentId={instrument.instrumentId}
                    field="exitDistributionByTrancheDiscountRateOverrides"
                    label={`${l('_Tranche')} ${trancheIndex + 1}`}
                    trancheId={tranche.id}
                    trancheIndex={trancheIndex}
                    indented
                    {...categoryRowProps}
                  />
                  {shouldShowOriginalRates(
                    'exitDistributionByTrancheDiscountRateOverrides',
                    tranche.id
                  ) && (
                    <OriginalDiscountRateRows
                      instrumentId={instrument.instrumentId}
                      field="exitDistributionByTrancheDiscountRateOverrides"
                      casesStructure={casesStructure}
                      initializedCases={initializedCases}
                      findSortedCaseIndex={findSortedCaseIndex}
                      trancheId={tranche.id}
                      trancheIndex={trancheIndex}
                    />
                  )}
                </Fragment>
              ))}
            </>
          ) : (
            <>
              <DiscountRateOverrideCategoryRows
                instrumentId={instrument.instrumentId}
                field="exitDistributionByTrancheDiscountRateOverrides"
                label={l('_ExitDistributions')}
                {...categoryRowProps}
              />
              {shouldShowOriginalRates(
                'exitDistributionByTrancheDiscountRateOverrides',
                TrancheKey.DefaultTrancheKey
              ) && (
                <OriginalDiscountRateRows
                  instrumentId={instrument.instrumentId}
                  field="exitDistributionByTrancheDiscountRateOverrides"
                  casesStructure={casesStructure}
                  initializedCases={initializedCases}
                  findSortedCaseIndex={findSortedCaseIndex}
                />
              )}
            </>
          )}
        </>
      )}

      <tr>
        <td
          colSpan={columnsCount}
          className="table-primary__cell--section-separator"
          style={{ backgroundColor: 'white' }}
        />
      </tr>
    </>
  );
};

export default DiscountRateOverrideInstrumentSection;
